/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { useQuery } from '@apollo/client';

import * as Types from '@xing-com/crate-common-graphql-types';
import type { MasterDirectoryEntryType } from '@xing-com/crate-common-graphql-types';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import {
  MasterDirectoryEntryByLetterDocument,
  MasterDirectoryOtherEntriesDocument,
} from '@xing-com/crate-master-directory-graphql';

type EntryByLetterParams = {
  letter: string;
  type: MasterDirectoryEntryType;
};

export const useGetEntriesByLetter = ({
  letter,
  type,
}: EntryByLetterParams): {
  loading: boolean;
  collection: {
    displayName: string;
    url: string;
    moreUrl?: string;
  }[];
} => {
  const document =
    letter === 'weitere'
      ? MasterDirectoryOtherEntriesDocument
      : MasterDirectoryEntryByLetterDocument;
  const { loading, data } = useQuery(document, {
    variables: {
      letter,
      type,
    },
  });
  let collection: {
    displayName: string;
    url: string;
    moreUrl?: string;
  }[] = [];

  if (letter === 'weitere' && data) {
    // Data is of type MasterDirectoryOtherEntriesQuery
    (Object.keys(data) as Array<keyof typeof data>).forEach((key) => {
      collection.push(
        ...(
          (data[key] as Types.MasterDirectoryEntryList)?.collection || []
        ).map((entry) => ({
          displayName: entry?.displayName || '',
          url: entry?.url || '',
        }))
      );
    });
  } else if (letter !== 'weitere' && data) {
    // Data is of type MasterDirectoryEntryByLetterQuery
    const byLetterData = data as Types.Query;
    collection = (
      byLetterData.masterDirectoryEntryByLetter?.collection || []
    ).map((entry) => ({
      displayName: entry?.displayName || '',
      url: entry?.url || '',
    }));
  }

  collection = collection
    .filter((entry): entry is Types.MasterDirectoryEntry => entry !== null)
    .map((entry) => {
      let displayName = entry.displayName || '';

      if (
        type === Types.MasterDirectoryEntryType.JobRole ||
        type === Types.MasterDirectoryEntryType.Company
      ) {
        displayName = `${displayName} Jobs`;
      }
      if (type === Types.MasterDirectoryEntryType.Location) {
        displayName = `Jobs in ${displayName}`;
      }

      return {
        ...entry,
        displayName,
        url: entry.url || '',
        moreUrl: `${ROUTES.jobsDirectory}/${letter}/${(entry.displayName || '')
          .replace(/ /g, '-')
          .toLowerCase()}`,
      };
    });

  return { loading, collection };
};
